import {
  type ColumnRenderer,
  type ExplorerDataProvider,
  FilterTypes,
  ImgElement,
  NavigationExplorer,
  createInMemoryDataProvider,
  findAnywhereInStringCaseInsensitive,
  findExact,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import {
  PiletArtifactsDocument,
  type PiletArtifactsQuery,
  type PiletArtifactsQueryVariables,
} from 'generated/hosting-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { type ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDeploymentStateIconLabel } from '../../common/utils';
import classes from './PiletArtifactsExplorer.module.scss';
import type { PiletArtifact } from './PiletArtifactsExplorer.types';

const UsedInActiveDeploymentStatusRenderer: ColumnRenderer<PiletArtifact> = (
  _value,
  data,
): ReactNode => {
  const { label, icon } = getDeploymentStateIconLabel(
    data.usedInActiveDeployment,
  );
  return (
    <div className={classes.status_container}>
      <ImgElement
        src={icon}
        style={{ width: '20px', height: '20px' }}
        decorative={true}
      />
      <div className={classes.status}>{label}</div>
    </div>
  );
};

export const PiletArtifactsExplorer: React.FC = (props) => {
  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const [filteredData, setFilteredData] = useState<PiletArtifact[]>([]);

  // Data provider
  const dataProvider: ExplorerDataProvider<PiletArtifact> = useMemo(() => {
    return {
      loadData: async ({ pagingInformation, filters, sorting }) => {
        if (filteredData.length === 0) {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );
          const result = await envApolloClient?.query<
            PiletArtifactsQuery,
            PiletArtifactsQueryVariables
          >({
            query: PiletArtifactsDocument,
            variables: {
              id: customizableServiceId,
            },
            fetchPolicy: 'network-only',
          });
          const piletArtifactData: React.SetStateAction<
            PiletArtifact[] | undefined
          > = [];
          result?.data?.serviceDefinition?.servicePiletArtifacts.nodes.map(
            (item, index) => {
              const piletData = {
                id: index,
                name: item.name,
                packageVersion: item.packageVersion,
                usedInActiveDeployment:
                  result?.data?.serviceDefinition?.serviceDeployments?.nodes[0]?.servicePiletArtifactIds?.includes(
                    item.id,
                  ) ?? false,
              };
              piletArtifactData.push(piletData);
              setFilteredData(piletArtifactData);
            },
          );
          return {
            totalCount: piletArtifactData.length,
            filteredCount: piletArtifactData.length,
            data: piletArtifactData,
            pagingInformation: 1,
            hasMoreData: false,
          };
        } else {
          const actualProvider = createInMemoryDataProvider<PiletArtifact>(
            filteredData ?? [],
            {
              filterFunctions: {
                name: findAnywhereInStringCaseInsensitive,
                packageVersion: findAnywhereInStringCaseInsensitive,
                usedInActiveDeployment: findExact,
              },
            },
          );
          // Increase page count
          const currentPage = (pagingInformation as number) ?? 0;

          // Use the in memory provider to do the heavy lifting
          const { totalCount, filteredCount, data } =
            await actualProvider.loadData({
              pagingInformation,
              filters,
              sorting,
            });

          // Apply paging to the results
          const pageSize = 20;
          return {
            totalCount: totalCount,
            filteredCount: filteredCount,
            data: data?.slice(
              pageSize * currentPage,
              pageSize * (currentPage + 1),
            ),
            pagingInformation: currentPage + 1,
            hasMoreData: currentPage < 9,
          };
        }
      },
    };
  }, [customizableServiceId, envId, filteredData]);

  return (
    <NavigationExplorer<PiletArtifact>
      {...props}
      stationKey={'PiletArtifactsExplorer'}
      title="Pilet Artifacts"
      columns={[
        {
          label: 'Package Name',
          propertyName: 'name',
          size: '2fr',
        },
        {
          label: 'Package Version',
          propertyName: 'packageVersion',
          size: '2fr',
        },
        {
          label: 'Deployment State',
          propertyName: 'usedInActiveDeployment',
          render: UsedInActiveDeploymentStatusRenderer,
        },
      ]}
      dataProvider={dataProvider}
      filterOptions={[
        {
          label: 'Package Name',
          property: 'name',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Package Version',
          property: 'packageVersion',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Deployment State',
          property: 'usedInActiveDeployment',
          type: FilterTypes.Options,
          options: [
            {
              label: 'Deployed',
              value: true,
            },
            {
              label: 'Not Deployed',
              value: false,
            },
          ],
        },
      ]}
    />
  );
};

export const PiletArtifactsExplorerCrumb: BreadcrumbResolver = () =>
  'Pilet Artifacts';
